import { z } from 'zod/v4';

import { NO_ALGORITHM_APPLIED_FILTER_VALUE } from './common';

import { CommonSearchParamsSchema } from '@/shared/utils/schemas';

export enum ALGORITHM_LEVEL {
  ADVERTISER = 'advertiser',
  CAMPAIGN = 'campaign',
  INSERTION_ORDER = 'insertion_order',
  ADGROUP = 'adgroup',
  ADSET = 'adset',
}
const AlgorithmLevelSchema = z.enum(ALGORITHM_LEVEL);
export type TAlgorithmLevel = z.infer<typeof AlgorithmLevelSchema>;

const AlgorithmStatusSchema = z.enum(['pending', 'active', 'pending_removal']);
export type AlgorithmStatus = z.infer<typeof AlgorithmStatusSchema>;

const AlgorithmIdFilterValueSchema = z.union([
  z.number(),
  z.literal(NO_ALGORITHM_APPLIED_FILTER_VALUE),
]);

export type AlgorithmIdFilterValue = z.infer<
  typeof AlgorithmIdFilterValueSchema
>;

const AlgorithmParamsSchema = z.object({
  algorithmId: z
    .array(AlgorithmIdFilterValueSchema)
    .optional()
    .catch(undefined),
  status: z
    .array(AlgorithmStatusSchema)
    .max(3)
    .refine((arr) => new Set(arr).size === arr.length)
    .optional()
    .catch(undefined),
  level: z
    .array(AlgorithmLevelSchema)
    .max(3)
    .refine((arr) => new Set(arr).size === arr.length)
    .optional()
    .catch(undefined),
});

export type AlgorithmStatuses = z.infer<typeof AlgorithmParamsSchema>['status'];
export type AlgorithmLevels = z.infer<typeof AlgorithmParamsSchema>['level'];

const MediaPlanContextParamsSchema = z.object({
  mediaPlan: z.coerce.number().optional().catch(undefined),
});

const baseOrdering = ['name', '-name', 'id', '-id'] as const;

const algorithmNameOrdering = [
  'algorithms__algorithm__name',
  '-algorithms__algorithm__name',
] as const;

const campaignRefOrdering = [
  'campaign__name',
  '-campaign__name',
  'campaign__id',
  '-campaign__id',
] as const;

const algorithmStatusOrdering = [
  'algorithms__status',
  '-algorithms__status',
] as const;

const dateOrdering = [
  'start_date',
  '-start_date',
  'end_date',
  '-end_date',
] as const;

export const AdvertiserSearchParamsSchema = CommonSearchParamsSchema.extend({
  ordering: z
    .enum([
      ...baseOrdering,
      'advertiser_algorithms__status',
      '-advertiser_algorithms__status',
      'advertiser_algorithms__algorithm__name',
      '-advertiser_algorithms__algorithm__name',
    ])
    .optional()
    .catch(undefined),
  ...AlgorithmParamsSchema.shape,
  ...MediaPlanContextParamsSchema.shape,
});

export type AdvertiserSearchParams = z.infer<
  typeof AdvertiserSearchParamsSchema
>;
export type AdvertiserSorting = z.infer<
  typeof AdvertiserSearchParamsSchema
>['ordering'];

export const CampaignSearchParamsSchema = CommonSearchParamsSchema.extend({
  ordering: z
    .enum([
      ...baseOrdering,
      ...algorithmNameOrdering,
      ...algorithmStatusOrdering,
      ...dateOrdering,
      'advertiser__name',
      '-advertiser__name',
      'advertiser__id',
      '-advertiser__id',
      'account__name',
      '-account__name',
      'account__id',
      '-account__id',
      'campaign_metadata__start_date',
      '-campaign_metadata__start_date',
      'campaign_metadata__end_date',
      '-campaign_metadata__end_date',
      'start_date',
      '-start_date',
      'end_date',
      '-end_date',
    ])
    .optional()
    .catch(undefined),
  budgetMin: z.coerce.number().min(0).optional().catch(undefined),
  budgetMax: z.coerce.number().min(0).optional().catch(undefined),
  startDate: z.iso.date().optional().catch(undefined),
  endDate: z.iso.date().optional().catch(undefined),
  ...AlgorithmParamsSchema.shape,
  ...MediaPlanContextParamsSchema.shape,
});

export type CampaignSearchParams = z.infer<typeof CampaignSearchParamsSchema>;
export type CampaignSorting = z.infer<
  typeof CampaignSearchParamsSchema
>['ordering'];

export const InsertionOrderSearchParamsSchema = CommonSearchParamsSchema.extend(
  {
    ordering: z
      .enum([
        ...baseOrdering,
        ...algorithmNameOrdering,
        ...campaignRefOrdering,
        ...dateOrdering,
        'budget',
        '-budget',
      ])
      .optional()
      .catch(undefined),
    ...AlgorithmParamsSchema.shape,
  }
);

export type InsertionOrderSearchParams = z.infer<
  typeof InsertionOrderSearchParamsSchema
>;
export type InsertionOrderSorting = z.infer<
  typeof InsertionOrderSearchParamsSchema
>['ordering'];

export const AdGroupSearchParamsSchema = CommonSearchParamsSchema.extend({
  brand: z.string().optional().catch(undefined),
  ordering: z
    .enum([
      ...baseOrdering,
      ...algorithmNameOrdering,
      ...campaignRefOrdering,
      ...algorithmStatusOrdering,
      'meta_campaign__name',
      '-meta_campaign__name',
      'meta_campaign__id',
      '-meta_campaign__id',
      'campaign__advertiser__name',
      '-campaign__advertiser__name',
      'campaign__advertiser__id',
      '-campaign__advertiser__id',
      'campaign__account__name',
      '-campaign__account__name',
      'campaign__account__id',
      '-campaign__account__id',
    ])
    .optional()
    .catch(undefined),
  ...AlgorithmParamsSchema.shape,
  ...MediaPlanContextParamsSchema.shape,
});

export type AdGroupSearchParams = z.infer<typeof AdGroupSearchParamsSchema>;

export type AdGroupSorting = z.infer<
  typeof AdGroupSearchParamsSchema
>['ordering'];
